/**
 * Date and time utility functions for consistent timestamp formatting
 * Converts UTC timestamps to local time for better user experience
 */

/**
 * Format timestamp to local date and time
 * @param {string|Date} timestamp - ISO timestamp or Date object
 * @param {Object} options - Formatting options
 * @returns {string} Formatted local date and time
 */
export const formatLocalDateTime = (timestamp, options = {}) => {
  if (!timestamp) return 'N/A';
  
  const date = new Date(timestamp);
  if (isNaN(date.getTime())) return 'Invalid Date';
  
  const defaultOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
    timeZoneName: 'short'
  };
  
  return date.toLocaleString('en-US', { ...defaultOptions, ...options });
};

/**
 * Format timestamp to local date only
 * @param {string|Date} timestamp - ISO timestamp or Date object
 * @param {Object} options - Formatting options
 * @returns {string} Formatted local date
 */
export const formatLocalDate = (timestamp, options = {}) => {
  if (!timestamp) return 'N/A';
  
  const date = new Date(timestamp);
  if (isNaN(date.getTime())) return 'Invalid Date';
  
  const defaultOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  };
  
  return date.toLocaleDateString('en-US', { ...defaultOptions, ...options });
};

/**
 * Format timestamp to local time only
 * @param {string|Date} timestamp - ISO timestamp or Date object
 * @param {Object} options - Formatting options
 * @returns {string} Formatted local time
 */
export const formatLocalTime = (timestamp, options = {}) => {
  if (!timestamp) return 'N/A';
  
  const date = new Date(timestamp);
  if (isNaN(date.getTime())) return 'Invalid Time';
  
  const defaultOptions = {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  };
  
  return date.toLocaleTimeString('en-US', { ...defaultOptions, ...options });
};

/**
 * Format timestamp for compact display (date + time on separate lines)
 * @param {string|Date} timestamp - ISO timestamp or Date object
 * @returns {Object} Object with date and time strings
 */
export const formatCompactDateTime = (timestamp) => {
  if (!timestamp) return { date: 'N/A', time: 'N/A' };
  
  const date = new Date(timestamp);
  if (isNaN(date.getTime())) return { date: 'Invalid', time: 'Date' };
  
  return {
    date: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
    time: date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true })
  };
};

/**
 * Format relative time (e.g., "2 hours ago", "Yesterday")
 * @param {string|Date} timestamp - ISO timestamp or Date object
 * @returns {string} Relative time string
 */
export const formatRelativeTime = (timestamp) => {
  if (!timestamp) return 'N/A';
  
  const date = new Date(timestamp);
  if (isNaN(date.getTime())) return 'Invalid Date';
  
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMinutes = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  
  if (diffMinutes < 1) {
    return 'Just now';
  } else if (diffMinutes < 60) {
    return `${diffMinutes} minute${diffMinutes === 1 ? '' : 's'} ago`;
  } else if (diffHours < 24) {
    return `${diffHours} hour${diffHours === 1 ? '' : 's'} ago`;
  } else if (diffDays === 1) {
    return 'Yesterday';
  } else if (diffDays < 7) {
    return `${diffDays} day${diffDays === 1 ? '' : 's'} ago`;
  } else {
    return formatLocalDate(timestamp);
  }
};

/**
 * Format timestamp for table display (compact format)
 * @param {string|Date} timestamp - ISO timestamp or Date object
 * @returns {string} Formatted timestamp for tables
 */
export const formatTableDateTime = (timestamp) => {
  if (!timestamp) return 'N/A';
  
  const date = new Date(timestamp);
  if (isNaN(date.getTime())) return 'Invalid Date';
  
  const now = new Date();
  const isToday = date.toDateString() === now.toDateString();
  const isYesterday = new Date(now.getTime() - 24 * 60 * 60 * 1000).toDateString() === date.toDateString();
  
  if (isToday) {
    return `Today ${date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true })}`;
  } else if (isYesterday) {
    return `Yesterday ${date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true })}`;
  } else {
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit', 
      minute: '2-digit', 
      hour12: true 
    });
  }
};

/**
 * Get user's timezone
 * @returns {string} User's timezone
 */
export const getUserTimezone = () => {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
};

/**
 * Format timestamp with timezone info
 * @param {string|Date} timestamp - ISO timestamp or Date object
 * @returns {string} Formatted timestamp with timezone
 */
export const formatWithTimezone = (timestamp) => {
  if (!timestamp) return 'N/A';
  
  const date = new Date(timestamp);
  if (isNaN(date.getTime())) return 'Invalid Date';
  
  const timezone = getUserTimezone();
  return `${formatLocalDateTime(timestamp)} (${timezone})`;
};

/**
 * Check if timestamp is today
 * @param {string|Date} timestamp - ISO timestamp or Date object
 * @returns {boolean} True if timestamp is today
 */
export const isToday = (timestamp) => {
  if (!timestamp) return false;
  
  const date = new Date(timestamp);
  const today = new Date();
  
  return date.toDateString() === today.toDateString();
};

/**
 * Check if timestamp is in the past
 * @param {string|Date} timestamp - ISO timestamp or Date object
 * @returns {boolean} True if timestamp is in the past
 */
export const isPast = (timestamp) => {
  if (!timestamp) return false;
  
  const date = new Date(timestamp);
  const now = new Date();
  
  return date.getTime() < now.getTime();
};

/**
 * Check if timestamp is in the future
 * @param {string|Date} timestamp - ISO timestamp or Date object
 * @returns {boolean} True if timestamp is in the future
 */
export const isFuture = (timestamp) => {
  if (!timestamp) return false;
  
  const date = new Date(timestamp);
  const now = new Date();
  
  return date.getTime() > now.getTime();
};
